import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Share2, CalendarCheck, Sparkles, Clock, ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import Seo from "@/components/Seo";
import AdminEditableImage from "@/components/AdminEditableImage";
import { pageTitle, truncate } from "@/lib/seo";
import { useSiteContent, scValue, useCmsItems } from "@/hooks/useSiteContent";

// Meta TECNICA (icona, immagine, posizione) mappata per chiave. Testi/CTA/badge vengono dal CMS.
const SERVICE_META = {
  cv: { icon: FileText, img: "/smart-services-backgrounds/smart-service-cv.webp", pos: "70% center", editableKey: "smart_cv_image" },
  flyer: { icon: Share2, img: "/smart-services-backgrounds/smart-service-social-flyer.webp", pos: "75% center", editableKey: "smart_flyer_image" },
  booking: { icon: CalendarCheck, img: "/smart-services-backgrounds/smart-service-hospitality.webp", pos: "72% center", editableKey: "smart_hospitality_image" },
};

const CARD_LIGHT_GRADIENT = "linear-gradient(90deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.58) 44%, rgba(255,255,255,0.12) 72%, rgba(255,255,255,0) 100%)";

const SmartServices = () => {
  const { t, lang } = useI18n();
  const sc = useSiteContent();
  const ps = sc?.page_smart;
  const cards = useCmsItems("smart_service");
  const sv = (base, tkey) => scValue(ps, base, lang, t(tkey));
  const cv = (d, base) => (lang === "en" ? d[`${base}_en`] : d[`${base}_it`]) || d[`${base}_it`] || d[`${base}_en`] || "";
  return (
    <div data-testid="smart-services-page">
      <Seo
        title={pageTitle(sv("title", "smart.title"))}
        description={truncate(`${sv("subtitle", "smart.subtitle")} ${t("faq.a4")}`)}
        path="/servizi-smart"
        type="website"
      />
      <section className="relative bg-brand-navy text-white overflow-hidden group">
        <AdminEditableImage as="background" imageKey="smart_services_hero" defaultSrc="/smart-services-backgrounds/smart-services-hero.webp"
          selfGroup={false} className="absolute inset-0" style={{ backgroundColor: "#050d1f" }} />
        <div className="absolute inset-0" aria-hidden="true" style={{ background: "linear-gradient(90deg, rgba(5,13,31,0.52) 0%, rgba(5,13,31,0.20) 42%, rgba(5,13,31,0.04) 70%, rgba(5,13,31,0) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-orange">
            <Sparkles className="h-3.5 w-3.5" /> {sv("badge", "smart.badge")}
          </span>
          <h1 className="mt-6 text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{sv("title", "smart.title")}</h1>
          <p className="mt-4 text-white/70 max-w-2xl">{sv("subtitle", "smart.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {(cards.length ? cards : []).map((it, i) => {
            const d = it.data || {};
            const meta = SERVICE_META[d.key] || {};
            const Icon = meta.icon || Sparkles;
            const title = cv(d, "title");
            const desc = cv(d, "desc");
            const ctaLabel = cv(d, "button_label");
            const url = d.url || "";
            const isExternal = /^https?:\/\//i.test(url);
            const badge = d.badge || "";
            return (
              <motion.div key={it.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                data-testid={`smart-service-${i}`}
                style={meta.editableKey ? { backgroundColor: "#ffffff" } : { backgroundColor: "#ffffff" }}
                className="group relative overflow-hidden rounded-2xl border border-brand-navy/10 p-8 lift">
                {meta.editableKey && (
                  <AdminEditableImage as="background" imageKey={meta.editableKey} defaultSrc={meta.img} selfGroup={false}
                    className="absolute inset-0"
                    style={{ backgroundSize: "cover", backgroundPosition: meta.pos, backgroundColor: "#ffffff" }} />
                )}
                <div className="absolute inset-0" aria-hidden="true" style={{ background: CARD_LIGHT_GRADIENT }} />
                {badge === "premium" ? (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-brand-electric px-2.5 py-1 rounded-full">
                    <Sparkles className="h-3 w-3" /> Premium
                  </span>
                ) : badge === "new" ? (
                  <span data-testid="badge-nuovo" className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-brand-orange px-2.5 py-1 rounded-full">
                    <Sparkles className="h-3 w-3" /> NUOVO
                  </span>
                ) : badge === "soon" ? (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-brand-orange bg-brand-orange/10 px-2.5 py-1 rounded-full">
                    <Clock className="h-3 w-3" /> {t("common.comingSoon")}
                  </span>
                ) : null}
                <div className="relative">
                  <div className="h-14 w-14 rounded-2xl bg-brand-electric/10 flex items-center justify-center text-brand-electric"><Icon className="h-7 w-7" /></div>
                  <h3 className="mt-6 font-heading font-semibold text-xl text-brand-navy">{title}</h3>
                  <p className="mt-3 text-brand-orange leading-relaxed font-bold">{desc}</p>
                  {url && (isExternal ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="booking-smart-open"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-electric hover:text-brand-orange transition-colors">
                      {ctaLabel || t("cv.generateShort")}
                    </a>
                  ) : (
                    <Link to={url} data-testid={`smart-open-${d.key || i}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-electric hover:text-brand-orange transition-colors">
                      {ctaLabel || t("cv.generateShort")} <ArrowRight className="h-4 w-4" />
                    </Link>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-12 rounded-2xl bg-brand-light border border-brand-navy/10 p-8 text-center">
          <p className="text-brand-navy/70">{sv("notify", "smart.notify")}</p>
        </div>
      </div>
    </div>
  );
};

export default SmartServices;
